import api from './api';

const portfolioService = {
  // Get all portfolios with optional filtering and statistics
  getAllPortfolios: async (params = {}) => {
    const response = await api.get('/portfolios', { params });
    return {
      portfolios: response.data.portfolios || [],
      total: response.data.total || 0,
      stats: response.data.stats
    };
  },

  // Get portfolio by ID with full details
  getPortfolioById: async (id) => {
    const response = await api.get(`/portfolios/${id}`);
    return response.data;
  },

  // Create new portfolio
  createPortfolio: async (portfolioData) => {
    const response = await api.post('/portfolios', portfolioData);
    return response.data;
  },

  // Update portfolio
  updatePortfolio: async (id, portfolioData) => {
    const response = await api.put(`/portfolios/${id}`, portfolioData);
    return response.data;
  },

  // Delete portfolio (soft delete)
  deletePortfolio: async (id) => {
    const response = await api.delete(`/portfolios/${id}`);
    return response.data;
  },

  // Get all projects in portfolio
  getPortfolioProjects: async (id, params = {}) => {
    const response = await api.get(`/portfolios/${id}/projects`, { params });
    return {
      projects: response.data.projects || [],
      total: response.data.total || 0
    };
  },

  // Get portfolio statistics
  getPortfolioStats: async (id) => {
    const response = await api.get(`/portfolios/${id}/stats`);
    return response.data.stats || {};
  },

  // Get portfolio performance metrics
  getPortfolioPerformance: async (id) => {
    const response = await api.get(`/portfolios/${id}/performance`);
    return response.data.performance || {};
  },

  // Add project to portfolio
  addProject: async (portfolioId, projectId) => {
    const response = await api.post(`/portfolios/${portfolioId}/projects`, { project_id: projectId });
    return response.data;
  },

  // Remove project from portfolio
  removeProject: async (portfolioId, projectId) => {
    const response = await api.delete(`/portfolios/${portfolioId}/projects/${projectId}`);
    return response.data;
  },

  // Helper: Calculate budget utilization percentage
  calculateBudgetUtilization: (budgetAllocated, budgetSpent) => {
    if (!budgetAllocated || budgetAllocated === 0) return 0;
    return ((budgetSpent / budgetAllocated) * 100).toFixed(2);
  },

  // Helper: Calculate overall health based on project health
  calculateOverallHealth: (projects) => {
    if (!projects || projects.length === 0) return 'On Track';
    
    const healthyCount = projects.filter(p => p.health_status === 'Green').length;
    const healthPercentage = (healthyCount / projects.length) * 100;
    
    if (healthPercentage > 70) return 'On Track';
    if (healthPercentage >= 40) return 'At Risk';
    return 'Off Track';
  },

  // Helper: Format budget as currency
  formatBudget: (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  },

  // Helper: Format percentage
  formatPercentage: (value, decimals = 1) => {
    return `${parseFloat(value || 0).toFixed(decimals)}%`;
  },

  // Helper: Get status color
  getStatusColor: (status) => {
    const colors = {
      'Planning': '#6b7280',
      'Active': '#10b981',
      'On Hold': '#f59e0b',
      'Completed': '#3b82f6',
      'Cancelled': '#ef4444'
    };
    return colors[status] || '#6b7280';
  },

  // Helper: Get health color
  getHealthColor: (health) => {
    const colors = {
      'On Track': '#10b981',
      'At Risk': '#f59e0b',
      'Off Track': '#ef4444'
    };
    return colors[health] || '#6b7280';
  },

  // Helper: Get budget status color
  getBudgetStatusColor: (utilization) => {
    if (utilization < 70) return '#10b981'; // green
    if (utilization < 90) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  },

  // Helper: Get budget status
  getBudgetStatus: (utilization) => {
    if (utilization < 70) return 'good';
    if (utilization < 90) return 'warning';
    return 'critical';
  },

  // Helper: Aggregate project metrics
  aggregateProjectMetrics: (projects) => {
    if (!projects || projects.length === 0) {
      return {
        total: 0,
        active: 0,
        completed: 0,
        onHold: 0,
        cancelled: 0,
        healthy: 0,
        atRisk: 0,
        offTrack: 0,
        avgProgress: 0,
        totalBudgetAllocated: 0,
        totalBudgetSpent: 0
      };
    }

    const metrics = {
      total: projects.length,
      active: projects.filter(p => p.status === 'Active').length,
      completed: projects.filter(p => p.status === 'Completed').length,
      onHold: projects.filter(p => p.status === 'On Hold').length,
      cancelled: projects.filter(p => p.status === 'Cancelled').length,
      healthy: projects.filter(p => p.health_status === 'Green').length,
      atRisk: projects.filter(p => p.health_status === 'Yellow').length,
      offTrack: projects.filter(p => p.health_status === 'Red').length,
      avgProgress: (projects.reduce((sum, p) => sum + (p.progress_percentage || 0), 0) / projects.length).toFixed(1),
      totalBudgetAllocated: projects.reduce((sum, p) => sum + parseFloat(p.budget_allocated || 0), 0),
      totalBudgetSpent: projects.reduce((sum, p) => sum + parseFloat(p.budget_spent || 0), 0)
    };

    return metrics;
  },

  // Helper: Calculate KPI status
  calculateKPIStatus: (target, actual, type = 'higher_is_better') => {
    if (type === 'higher_is_better') {
      if (actual >= target * 0.9) return 'on_track';
      if (actual >= target * 0.7) return 'at_risk';
      return 'off_track';
    } else {
      // lower_is_better (e.g., for risk exposure)
      if (actual <= target * 1.1) return 'on_track';
      if (actual <= target * 1.3) return 'at_risk';
      return 'off_track';
    }
  },

  // Helper: Get KPI status color
  getKPIStatusColor: (status) => {
    const colors = {
      'on_track': '#10b981',
      'at_risk': '#f59e0b',
      'off_track': '#ef4444'
    };
    return colors[status] || '#6b7280';
  },

  // Helper: Format strategic goal
  formatStrategicGoal: (goal) => {
    return {
      id: goal.id,
      title: goal.title,
      description: goal.description,
      target_date: goal.target_date,
      status: goal.status || 'not_started',
      progress: goal.progress || 0
    };
  },

  // Helper: Get strategic goal status color
  getGoalStatusColor: (status) => {
    const colors = {
      'not_started': '#6b7280',
      'in_progress': '#3b82f6',
      'completed': '#10b981',
      'on_hold': '#f59e0b',
      'cancelled': '#ef4444'
    };
    return colors[status] || '#6b7280';
  },

  // Helper: Calculate days until date
  daysUntil: (date) => {
    if (!date) return null;
    const targetDate = new Date(date);
    const today = new Date();
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  },

  // Helper: Format date range
  formatDateRange: (startDate, endDate) => {
    if (!startDate && !endDate) return 'No dates set';
    
    const formatDate = (date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    };

    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (startDate) {
      return `From ${formatDate(startDate)}`;
    } else {
      return `Until ${formatDate(endDate)}`;
    }
  },

  // Helper: Check if portfolio is overdue
  isOverdue: (endDate, status) => {
    if (!endDate || status === 'Completed' || status === 'Cancelled') return false;
    return new Date(endDate) < new Date();
  },

  // Helper: Sort portfolios
  sortPortfolios: (portfolios, sortBy = 'name', ascending = true) => {
    return [...portfolios].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'budget':
          comparison = parseFloat(a.budget_allocated || 0) - parseFloat(b.budget_allocated || 0);
          break;
        case 'projects':
          comparison = (a.projects?.length || 0) - (b.projects?.length || 0);
          break;
        case 'created':
          comparison = new Date(a.created_at) - new Date(b.created_at);
          break;
        default:
          comparison = 0;
      }
      
      return ascending ? comparison : -comparison;
    });
  },

  // Helper: Filter portfolios
  filterPortfolios: (portfolios, filters) => {
    let filtered = [...portfolios];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(term) ||
        p.code?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(p => p.status === filters.status);
    }

    if (filters.owner_id) {
      filtered = filtered.filter(p => p.owner_id === filters.owner_id);
    }

    if (filters.is_active !== undefined) {
      filtered = filtered.filter(p => p.is_active === filters.is_active);
    }

    return filtered;
  },

  // Helper: Get portfolio summary
  getPortfolioSummary: (portfolio) => {
    const projects = portfolio.projects || [];
    const metrics = portfolioService.aggregateProjectMetrics(projects);
    const budgetUtilization = portfolioService.calculateBudgetUtilization(
      portfolio.budget_allocated,
      metrics.totalBudgetSpent
    );

    return {
      id: portfolio.id,
      name: portfolio.name,
      code: portfolio.code,
      status: portfolio.status,
      owner: portfolio.owner,
      projectCount: metrics.total,
      activeProjects: metrics.active,
      completedProjects: metrics.completed,
      budgetAllocated: parseFloat(portfolio.budget_allocated || 0),
      budgetSpent: metrics.totalBudgetSpent,
      budgetUtilization: parseFloat(budgetUtilization),
      overallHealth: portfolioService.calculateOverallHealth(projects),
      avgProgress: parseFloat(metrics.avgProgress),
      strategicGoalsCount: Array.isArray(portfolio.strategic_goals) ? portfolio.strategic_goals.length : 0
    };
  },

  // Helper: Validate portfolio data
  validatePortfolioData: (data) => {
    const errors = {};

    if (!data.name || data.name.trim() === '') {
      errors.name = 'Portfolio name is required';
    }

    if (data.budget_allocated && data.budget_allocated < 0) {
      errors.budget_allocated = 'Budget allocated must be >= 0';
    }

    if (data.budget_spent && data.budget_spent < 0) {
      errors.budget_spent = 'Budget spent must be >= 0';
    }

    if (data.budget_spent && data.budget_allocated && data.budget_spent > data.budget_allocated) {
      errors.budget_spent = 'Budget spent cannot exceed budget allocated';
    }

    if (data.start_date && data.end_date && new Date(data.start_date) > new Date(data.end_date)) {
      errors.end_date = 'End date must be after start date';
    }

    if (data.strategic_goals && !Array.isArray(data.strategic_goals)) {
      errors.strategic_goals = 'Strategic goals must be an array';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Helper: Get status options
  getStatusOptions: () => [
    { value: 'Planning', label: 'Planning' },
    { value: 'Active', label: 'Active' },
    { value: 'On Hold', label: 'On Hold' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' }
  ],

  // Helper: Get health status options
  getHealthStatusOptions: () => [
    { value: 'On Track', label: 'On Track', color: '#10b981' },
    { value: 'At Risk', label: 'At Risk', color: '#f59e0b' },
    { value: 'Off Track', label: 'Off Track', color: '#ef4444' }
  ]
};

export default portfolioService;

